import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  CheckCircle,
  Refresh,
  ExpandMore,
  ExpandLess,
  Groups,
} from '@mui/icons-material';

/**
 * Professional Accounts Manager with Advanced Features
 * Manages SMTP accounts, groups, testing, usage analytics
 */
const AccountsManager = ({
  accounts = [],
  accountGroups = [],
  onAddAccount = () => {},
  onEditAccount = () => {},
  onDeleteAccount = () => {},
  onTestAccount = () => {},
  onCreateGroup = () => {},
  onEditGroup = () => {},
  onDeleteGroup = () => {},
  selectedAccountId = '',
  onSelectAccount = () => {},
  bulkTesting = false,
  onBulkTest = () => {},
  accountUsageToday = {},
  campaignPlan = { perAccountDaily: 0 },
  compactView = false,
  onCompactViewChange = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [expandedAccountId, setExpandedAccountId] = useState(null);
  const [expandedGroupId, setExpandedGroupId] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  // Get unique providers
  const providers = [...new Set(accounts.map((a) => a.provider || 'SMTP'))];

  // Filter accounts
  const filteredAccounts = accounts.filter((a) => {
    const matchesSearch =
      !searchTerm ||
      (a.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.fromEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.username?.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesProvider = !providerFilter || a.provider === providerFilter;
    return matchesSearch && matchesProvider;
  });

  // Calculate usage stats
  const getUsagePercent = (accountId) => {
    const limit = campaignPlan.perAccountDaily || 100;
    const used = accountUsageToday[accountId] || 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageStatus = (accountId) => {
    const percent = getUsagePercent(accountId);
    if (percent > 90) return { color: '#f44336', label: 'Critical' };
    if (percent > 70) return { color: '#FF9800', label: 'High' };
    if (percent > 50) return { color: '#FFC107', label: 'Moderate' };
    return { color: '#4CAF50', label: 'Low' };
  };

  const accountMap = useMemo(() => {
    const map = new Map();
    accounts.forEach((acc) => map.set(acc.id, acc));
    return map;
  }, [accounts]);

  const getAccountHealth = (account) => {
    const status = account?.status || 'unknown';
    if (status === 'connected') {
      return { label: 'Working', color: '#16a34a' };
    }
    if (status === 'failed') {
      return { label: 'Not working', color: '#dc2626' };
    }
    return { label: 'Unknown', color: '#64748b' };
  };

  const visibleIds = filteredAccounts.map((a) => a.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedAccounts.includes(id));
  const someVisibleSelected =
    visibleIds.some((id) => selectedAccounts.includes(id)) && !allVisibleSelected;

  const toggleSelection = (id) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    setSelectedAccounts(filteredAccounts.map((a) => a.id));
  };

  const clearSelection = () => {
    setSelectedAccounts([]);
  };

  const handleCreateGroup = () => {
    if (!selectedAccounts.length) return;
    onCreateGroup(selectedAccounts);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 2 }}>
      {/* Header Section */}
      <Card
        variant="outlined"
        sx={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
          boxShadow: '0 2px 8px 0 rgba(59, 130, 246, 0.15)',
          borderColor: 'primary.light',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.25 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={isMobile ? 1 : 1.5}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant={isMobile ? 'subtitle1' : 'h5'} fontWeight={800} sx={{ mb: 0.25 }}>
                📧 Email Accounts
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                Manage SMTP accounts, groups, and sending quotas
              </Typography>
            </Box>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={1} 
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                size={isMobile ? 'small' : 'medium'}
                variant="contained"
                startIcon={<Add />}
                onClick={onAddAccount}
                fullWidth={isMobile}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {isMobile ? 'Add' : 'Add Account'}
              </Button>
              <Button
                size={isMobile ? 'small' : 'medium'}
                variant="outlined"
                startIcon={<Refresh />}
                onClick={onBulkTest}
                disabled={bulkTesting || filteredAccounts.length === 0}
                fullWidth={isMobile}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {bulkTesting ? 'Testing...' : isMobile ? 'Test' : 'Test All'}
              </Button>
            </Stack>
          </Stack>

          {/* Search & Filter Row */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={isMobile ? 0.75 : 1}
            sx={{ mt: isMobile ? 1 : 1.5 }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <TextField
              size="small"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: isMobile ? '100%' : 200 }}
              variant="outlined"
              InputProps={{
                endAdornment: searchTerm ? (
                  <IconButton size="small" onClick={() => setSearchTerm('')} edge="end">
                    <Close fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
            <TextField
              select
              size="small"
              label={isMobile ? 'Provider' : 'Provider'}
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              sx={{ minWidth: isMobile ? '100%' : 150 }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">All Providers</option>
              {providers.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  size={isMobile ? 'small' : 'medium'}
                  checked={compactView}
                  onChange={(e) => onCompactViewChange(e.target.checked)}
                />
              }
              label={isMobile ? 'Compact' : 'Compact View'}
              sx={{ m: 0, fontSize: isMobile ? '0.75rem' : 'inherit' }}
            />
          </Stack>

          {/* Statistics Row */}
          <Stack 
            direction={{ xs: 'row', sm: 'row' }} 
            spacing={isMobile ? 0.5 : 1} 
            sx={{ mt: isMobile ? 1 : 1.5 }} 
            flexWrap="wrap"
          >
            <Chip
              size={isMobile ? 'small' : 'medium'}
              label={`Total: ${accounts.length}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              size={isMobile ? 'small' : 'medium'}
              label={`Visible: ${filteredAccounts.length}`}
              variant="outlined"
            />
            <Chip
              size={isMobile ? 'small' : 'medium'}
              label={`Providers: ${providers.length}`}
              variant="outlined"
            />
          </Stack>

          {/* Selection & Group Controls */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={isMobile ? 0.75 : 1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mt: isMobile ? 1 : 1.5 }}
          >
            <Chip
              size={isMobile ? 'small' : 'medium'}
              label={`Selected: ${selectedAccounts.length}`}
              color={selectedAccounts.length ? 'primary' : 'default'}
              variant={selectedAccounts.length ? 'filled' : 'outlined'}
            />
            <Button 
              size={isMobile ? 'small' : 'medium'}
              variant="text" 
              onClick={selectAllVisible} 
              disabled={!filteredAccounts.length}
              sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
            >
              Select visible
            </Button>
            <Button 
              size={isMobile ? 'small' : 'medium'}
              variant="text" 
              onClick={clearSelection} 
              disabled={!selectedAccounts.length}
              sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
            >
              Clear
            </Button>
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<Groups />}
              onClick={handleCreateGroup}
              disabled={!selectedAccounts.length}
              fullWidth={isMobile}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isMobile ? 'Group' : 'Create group'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card 
        variant="outlined" 
        sx={{ 
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
          borderColor: 'primary.light',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.25 : 2 }}>
          <Stack spacing={isMobile ? 1 : 1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Groups fontSize={isMobile ? 'small' : 'medium'} color="primary" />
              <Typography variant={isMobile ? 'subtitle2' : 'h6'} fontWeight={700}>
                Account Groups
              </Typography>
              <Chip 
                size="small" 
                label={`${accountGroups.length}`} 
                color="primary" 
                variant="outlined"
              />
            </Stack>
            {accountGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1, textAlign: 'center' }}>
                No groups yet. Select accounts and create a group.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {accountGroups.map((group) => (
                  <Box key={group.id}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={isMobile ? 0.75 : 1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{
                        p: isMobile ? 0.75 : 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.paper',
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                        <Chip 
                          label={group.name} 
                          size={isMobile ? 'small' : 'medium'}
                          color="primary"
                          variant="filled"
                        />
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.65rem' : 'inherit' }}
                        >
                          {group.accountIds?.length || 0} accounts
                        </Typography>
                      </Stack>
                      <Stack 
                        direction="row" 
                        spacing={0.5} 
                        alignItems="center"
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        <Button
                          size={isMobile ? 'small' : 'small'}
                          variant="text"
                          onClick={() => setExpandedGroupId(expandedGroupId === group.id ? '' : group.id)}
                          sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                          {expandedGroupId === group.id ? 'Hide' : 'View'}
                        </Button>
                        <Button 
                          size={isMobile ? 'small' : 'small'}
                          variant="text" 
                          onClick={() => onEditGroup(group)}
                          sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                          Edit
                        </Button>
                        <Button 
                          size={isMobile ? 'small' : 'small'}
                          color="error" 
                          variant="text" 
                          onClick={() => onDeleteGroup(group.id)}
                          sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </Stack>
                    <Collapse in={expandedGroupId === group.id} unmountOnExit>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        {(group.accountIds || []).map((id) => {
                          const acc = accountMap.get(id);
                          if (!acc) return null;
                          const health = getAccountHealth(acc);
                          return (
                            <Stack
                              key={id}
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={isMobile ? 0.5 : 1}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              justifyContent="space-between"
                              sx={{ 
                                p: isMobile ? 0.5 : 0.75, 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: 0.75,
                                backgroundColor: 'background.default',
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {acc.label || acc.fromEmail || acc.username}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {acc.fromEmail || acc.username}
                                </Typography>
                              </Box>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                  size="small"
                                  label={health.label}
                                  sx={{ bgcolor: `${health.color}20`, color: health.color }}
                                />
                                <Button size="small" variant="text" onClick={() => onTestAccount(acc)}>
                                  Test
                                </Button>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    </Collapse>
                  </Box>
                ))}
              </Stack>
            )}
            <Divider />
            <Alert severity="info">
              Daily limit per account: <strong>{campaignPlan.perAccountDaily || 0}</strong> emails
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* Accounts List/Table */}
      {filteredAccounts.length === 0 ? (
        <Alert severity="warning">
          {accounts.length === 0
            ? 'No accounts configured. Add your first SMTP account to begin.'
            : 'No accounts match the current filter.'}
        </Alert>
      ) : (
        <>
          {/* Desktop View - Table */}
          {!isMobile && !compactView && (
            <TableContainer component={Card} variant="outlined">
              <Table size={isMobile ? 'small' : 'small'}>
                <TableHead>
                  <TableRow sx={{ 
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(37, 99, 235, 0.12) 100%)',
                    borderBottom: '2px solid primary.light'
                  }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={someVisibleSelected}
                        onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                      />
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      Label/Email
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      Provider
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      Today Usage
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      Status
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAccounts.map((account, idx) => {
                    const usagePercent = getUsagePercent(account.id);
                    const usageStatus = getUsageStatus(account.id);
                    const used = accountUsageToday[account.id] || 0;
                    const limit = campaignPlan.perAccountDaily || 100;
                    const isSelected = selectedAccounts.includes(account.id);

                    return (
                      <TableRow
                        key={account.id}
                        sx={{
                          backgroundColor:
                            selectedAccountId === account.id ? 'rgba(59, 130, 246, 0.08)' : 'inherit',
                          borderBottom: '1px solid rgba(0,0,0,0.06)',
                          '&:hover': { 
                            backgroundColor: 'rgba(59, 130, 246, 0.04)',
                            transition: 'background-color 0.2s'
                          },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => toggleSelection(account.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>
                              {account.label || account.fromEmail || account.username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                              {account.fromEmail || account.username}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                          <Chip
                            size="small"
                            label={account.provider || 'SMTP'}
                            variant="outlined"
                            sx={{ fontSize: isMobile ? '0.65rem' : 'inherit' }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                          <Stack spacing={0.5} alignItems="center">
                            <Typography variant="caption" fontWeight={700} sx={{ fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                              {used}/{limit}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent}
                              sx={{
                                width: isMobile ? 50 : 60,
                                height: 4,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: usageStatus.color,
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size={isMobile ? 'small' : 'small'}
                            label={usageStatus.label}
                            sx={{ 
                              backgroundColor: usageStatus.color + '20', 
                              color: usageStatus.color,
                              fontSize: isMobile ? '0.65rem' : 'inherit',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                            <Tooltip title="Use account">
                              <IconButton 
                                size={isMobile ? 'small' : 'small'} 
                                onClick={() => onSelectAccount(account.id)}
                                sx={{ padding: isMobile ? '4px' : 'inherit' }}
                              >
                                <CheckCircle
                                  fontSize={isMobile ? 'small' : 'small'}
                                  color={selectedAccountId === account.id ? 'success' : 'disabled'}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Test connection">
                              <IconButton 
                                size={isMobile ? 'small' : 'small'} 
                                onClick={() => onTestAccount(account.id)}
                                sx={{ padding: isMobile ? '4px' : 'inherit' }}
                              >
                                <Refresh fontSize={isMobile ? 'small' : 'small'} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton 
                                size={isMobile ? 'small' : 'small'} 
                                onClick={() => onEditAccount(account)}
                                sx={{ padding: isMobile ? '4px' : 'inherit' }}
                              >
                                <Edit fontSize={isMobile ? 'small' : 'small'} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size={isMobile ? 'small' : 'small'}
                                color="error" 
                                onClick={() => onDeleteAccount(account.id)}
                                sx={{ padding: isMobile ? '4px' : 'inherit' }}
                              >
                                <Delete fontSize={isMobile ? 'small' : 'small'} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile/Compact View - Cards */}
          {(isMobile || compactView) && (
            <Stack spacing={isMobile ? 1 : 1.5}>
              {filteredAccounts.map((account) => {
                const usagePercent = getUsagePercent(account.id);
                const usageStatus = getUsageStatus(account.id);
                const used = accountUsageToday[account.id] || 0;
                const limit = campaignPlan.perAccountDaily || 100;
                const isExpanded = expandedAccountId === account.id;
                const isSelected = selectedAccounts.includes(account.id);

                return (
                  <Card
                    key={account.id}
                    variant="outlined"
                    sx={{
                      backgroundColor:
                        selectedAccountId === account.id 
                          ? 'rgba(59, 130, 246, 0.08)' 
                          : 'background.paper',
                      borderColor:
                        selectedAccountId === account.id ? 'primary.main' : 'primary.light',
                      borderWidth: selectedAccountId === account.id ? 2 : 1,
                      transition: 'all 0.2s ease',
                      boxShadow: selectedAccountId === account.id 
                        ? '0 2px 8px rgba(59, 130, 246, 0.15)' 
                        : 'none',
                    }}
                  >
                    <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleSelection(account.id)}
                          sx={{ mt: 0, p: 0.5 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={700} variant={isMobile ? 'body2' : 'subtitle2'}>
                            {account.label || account.fromEmail || account.username}
                          </Typography>
                          <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip 
                              size="small" 
                              label={account.provider || 'SMTP'} 
                              variant="filled"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            <Chip
                              size="small"
                              label={usageStatus.label}
                              sx={{ 
                                backgroundColor: usageStatus.color + '20', 
                                color: usageStatus.color,
                                height: 20,
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}
                            />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {account.fromEmail || account.username}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                            <LinearProgress
                              variant="determinate"
                              value={usagePercent}
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: usageStatus.color,
                                  borderRadius: 1,
                                },
                              }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 45, textAlign: 'right', fontWeight: 600 }}>
                              {used}/{limit}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack direction="column" spacing={0.5} alignItems="flex-end">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setExpandedAccountId(isExpanded ? null : account.id)
                            }
                            sx={{ p: 0.5 }}
                          >
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            color={selectedAccountId === account.id ? 'success' : 'default'}
                            onClick={() => onSelectAccount(account.id)}
                            sx={{ p: 0.5 }}
                            title="Use this account"
                          >
                            <CheckCircle fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Collapse in={isExpanded}>
                        <Stack spacing={1} sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Refresh />}
                              onClick={() => onTestAccount(account.id)}
                              sx={{ flex: { xs: '0 0 calc(50% - 4px)', sm: 'auto' }, fontSize: '0.8rem' }}
                            >
                              Test
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => onEditAccount(account)}
                              sx={{ flex: { xs: '0 0 calc(50% - 4px)', sm: 'auto' }, fontSize: '0.8rem' }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => onDeleteAccount(account.id)}
                              sx={{ flex: { xs: '0 0 calc(50% - 4px)', sm: 'auto' }, fontSize: '0.8rem' }}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      </Collapse>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
};

export default AccountsManager;
